/**
 * WebSocket hook for real-time leaderboard updates.
 * Uses partysocket's ReconnectingWebSocket for automatic reconnection.
 */

import { useState, useEffect, useRef } from 'react';
import ReconnectingWebSocket from 'partysocket/ws';

// API base for WebSocket connection
const API_BASE = import.meta.env.VITE_SYNC_API_URL ||
  import.meta.env.VITE_PHOTO_API_URL ||
  'https://where-in-the-world-photos.armincerf.workers.dev';

export interface LeaderboardEntry {
  rank: number;
  countryCode: string;
  totalVotes: number;
  avgRating: number;
}

export interface StatsSummary {
  totalUsers: number;
  totalRatings: number;
  countriesRated: number;
}

interface LeaderboardState {
  leaderboard: LeaderboardEntry[];
  summary: StatsSummary | null;
  connected: boolean;
  error: string | null;
}

interface ServerMessage {
  type: 'init' | 'leaderboard_update';
  leaderboard: LeaderboardEntry[];
  summary: StatsSummary;
}

/**
 * Hook for real-time leaderboard updates via WebSocket.
 * Connects to the LeaderboardServer Durable Object and receives
 * instant updates when any user votes.
 */
export function useLeaderboardSocket(): LeaderboardState {
  const [state, setState] = useState<LeaderboardState>({
    leaderboard: [],
    summary: null,
    connected: false,
    error: null,
  });

  const socketRef = useRef<ReconnectingWebSocket | null>(null);

  useEffect(() => {
    // Convert HTTP URL to WebSocket URL
    const wsUrl = API_BASE.replace(/^http/, 'ws') + '/api/leaderboard/ws';

    // Use ReconnectingWebSocket for automatic reconnection
    const socket = new ReconnectingWebSocket(wsUrl, [], {
      maxRetries: 10,
      connectionTimeout: 5000,
    });

    socket.onopen = () => {
      setState(prev => ({ ...prev, connected: true, error: null }));
    };

    socket.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data as string) as ServerMessage;

        if (message.type === 'init' || message.type === 'leaderboard_update') {
          setState(prev => ({
            ...prev,
            leaderboard: message.leaderboard,
            summary: message.summary,
          }));
        }
      } catch (err) {
        console.error('Failed to parse WebSocket message:', err);
      }
    };

    socket.onerror = () => {
      setState(prev => ({ ...prev, error: 'Connection error' }));
    };

    socket.onclose = () => {
      setState(prev => ({ ...prev, connected: false }));
    };

    socketRef.current = socket;

    // Cleanup on unmount
    return () => {
      socket.close();
      socketRef.current = null;
    };
  }, []);

  return state;
}
